/**
 * Product Tags — user-defined labels a dealer creates once and applies to any
 * card, then filters inventory by.
 *
 * The values the backend accepts live here — the colour palette a tag may be
 * created with, the limits it enforces, the filter param. Endpoint PATHS do not:
 * they are written at the call site in `TagStore`, where the method and the body
 * are, so a request reads as one line instead of a lookup.
 */

/**
 * What the bulk endpoint does with the ids you send.
 *
 * `attach` and `detach` are ADDITIVE and SUBTRACTIVE — they touch only the tags
 * named — which is what the bulk bar needs: a tag left alone on forty cards must
 * stay exactly as it was on each of them. `sync` REPLACES every card's set with
 * the list, so it would wipe tags the user never looked at; nothing in this app
 * asks for that today.
 */
export const TAG_BULK_ACTIONS = Object.freeze({
  ATTACH: 'attach',
  DETACH: 'detach',
  SYNC:   'sync',
})

// ── Limits (enforced by the backend; mirrored here so the UI can say so first) ─
export const TAGS_MAX_PER_USER  = 200
export const TAGS_MAX_PER_CARD  = 20
// One bulk call covers at most this many cards. The bulk bar selects from a
// paged grid, so a selection can legitimately be larger — the store chunks.
export const TAGS_BULK_MAX_CARDS = 500

// ── Inventory filter ─────────────────────────────────────────────────────────
// The query param the Tags filter serializes to, and the sentinel for the
// "No tags" row.
//
// `tag_id` rides top-level (it is in BASE_PARAM_KEYS) rather than folding into
// the generic `filter=`, the same way `batch_id` does — the backend reads it as
// its own facet, comma-separated for multi-select.
export const TAGS_FILTER_PARAM = 'tag_id'

// Serialized inside the same param (`tag_id=none` / `tag_id=none,4`). A separate
// `untagged=1` param would have made "untagged OR tagged Graded" unrepresentable.
//
// NOT IN THE API CONTRACT. The row is therefore rendered only when the backend
// actually returns `untagged_count` in the facets — see `fetchTagOptions`. That
// way an unsupported `tag_id=none` can never be sent, and the row lights up on
// its own if the backend adds the count.
export const NO_TAGS_FILTER_KEY = 'none'
export const NO_TAGS_FILTER_LABEL = 'No tags'

// ── Settings · Manage tags ───────────────────────────────────────────────────
// The values `GET /tags` accepts for `sort`. The table header sends one of
// these; `order` carries the direction (`asc` / `desc`).
export const TAGS_SORT_KEYS = Object.freeze({
  NAME:       'name',
  CARDS:      'cards_count',
  CREATED_AT: 'created_at',
})

export const TAGS_DEFAULT_SORT = Object.freeze({ sortBy: TAGS_SORT_KEYS.NAME, sortDir: 'asc' })
export const TAGS_SETTINGS_PER_PAGE = 25

// Names are unique per account, so the duplicate is the one error this form can
// produce. One string, because the field's red state and the toast must say the
// same thing.
export const TAG_DUPLICATE_NAME_TITLE = 'Unable to save tag'
export const TAG_DUPLICATE_NAME_MESSAGE = "There's already a tag with this name."

// ── Colour palette ───────────────────────────────────────────────────────────
// The nine colours the create flow offers. A tag is not created until one is
// picked, so this list IS the create flow's second step.
//
// `value` is the app's own name for the colour — a palette key, and deliberately
// the matching `AppBadge` variant name, so anything that still wants a tinted
// pill gets one with no lookup table.
//
// `hex` is what the BACKEND stores: the API's colour field is a free `#RRGGBB`,
// not an enum. So the wire carries the hex and the UI carries the class, and
// `getTagColor` maps either direction. Two consequences worth knowing:
//   • the hexes below must equal what the classes actually paint, or a tag made
//     here would come back a slightly different colour. They are read from
//     `flowbite/plugin`, which supplies this app's palette and is the scale the
//     design file is drawn in — the same source the classes resolve against.
//   • a colour some other client invents is not in this list, so it falls back
//     to grey rather than rendering unstyled. Nothing is lost: the hex is still
//     on the tag, and adding a row here adopts it.
// These hexes are DATA (an API value), not styling — nothing may use them as an
// inline style; `swatchClass` is how a dot gets painted.
//
// `swatchClass` is the solid dot used by the picker, the chips and the filter
// rows. Shades are the Figma swatch tokens (node 28225:37632): the 500 step for
// every colour EXCEPT orange (400) and yellow (300), which read as muddy at 500
// against white. `chipClass` is the tinted pill the settings colour cell wears —
// the same bg/text pairs `AppBadge` uses, inlined because that cell needs the
// classes as a value rather than as a component.
export const TAG_COLORS = Object.freeze([
  { value: 'gray',   label: 'Grey',   hex: '#6B7280', swatchClass: 'bg-gray-500',   chipClass: 'bg-gray-100   text-gray-900'   },
  { value: 'green',  label: 'Green',  hex: '#0E9F6E', swatchClass: 'bg-green-500',  chipClass: 'bg-green-100  text-green-900'  },
  { value: 'blue',   label: 'Blue',   hex: '#3F83F8', swatchClass: 'bg-blue-500',   chipClass: 'bg-blue-100   text-blue-900'   },
  { value: 'red',    label: 'Red',    hex: '#F05252', swatchClass: 'bg-red-500',    chipClass: 'bg-red-100    text-red-900'    },
  { value: 'pink',   label: 'Pink',   hex: '#E74694', swatchClass: 'bg-pink-500',   chipClass: 'bg-pink-50    text-pink-900'   },
  { value: 'purple', label: 'Purple', hex: '#9061F9', swatchClass: 'bg-purple-500', chipClass: 'bg-purple-50  text-purple-900' },
  { value: 'indigo', label: 'Indigo', hex: '#6875F5', swatchClass: 'bg-indigo-500', chipClass: 'bg-indigo-50  text-indigo-900' },
  { value: 'orange', label: 'Orange', hex: '#FF8A4C', swatchClass: 'bg-orange-400', chipClass: 'bg-orange-100 text-orange-900' },
  { value: 'yellow', label: 'Yellow', hex: '#FACA15', swatchClass: 'bg-yellow-300', chipClass: 'bg-yellow-100 text-yellow-900' },
])

export const TAG_COLOR_VALUES = TAG_COLORS.map(({ value }) => value)

// Used only as a floor — the picker requires a real choice before a tag is
// created, so nothing should reach the API without one.
export const DEFAULT_TAG_COLOR = 'gray'

/**
 * Resolve a colour from EITHER form — the palette key the UI passes around, or
 * the `#RRGGBB` the API stores. One function, because a tag's `color` is a hex
 * when it comes off the wire and a key everywhere else, and a caller shouldn't
 * have to know which one it is holding.
 *
 * Falls back to the first colour rather than rendering an unstyled chip, so a
 * hex no palette row claims still reads as a tag.
 */
export const getTagColor = (value) => {
  const raw = String(value ?? '').trim().toLowerCase()

  return TAG_COLORS.find(color =>
    color.value === raw || color.hex.toLowerCase() === raw) ?? TAG_COLORS[0]
}

/** The `#RRGGBB` to send for a palette key (or for a hex, unchanged). */
export const tagColorHex = (value) => getTagColor(value).hex

/** The palette key for whatever a tag carries — the form every component wants. */
export const tagColorValue = (value) => getTagColor(value).value

// How much of a tag's name a CHIP shows before eliding. Not a limit on the name
// itself (see TAG_NAME_MAX_LENGTH) — a chip sits in a wrapping row beside other
// chips, and one long tag would otherwise push the rest onto its own line.
export const TAG_NAME_DISPLAY_MAX = 20

// The backend's limit. Shown as a counter rather than a hard `maxlength`, for
// the reason `AppCharacterCount` documents: silently truncating what someone
// typed is worse than telling them it's too long.
export const TAG_NAME_MAX_LENGTH = 64
